import pluralize from "pluralize";
import React, { useContext, useEffect, useState } from "react";
import { Card, Col, Row } from "react-bootstrap";
import styled from "styled-components";

import { FileRow } from "../../../common/components";
import { HorizontalDivider } from "../../../common/components/core";
import { useSelector, useDispatch } from "../../../redux/hooks";
import { currentRolesSelector } from "../../../redux/slices/auth/selectors";
import { getContactByAccountName } from "../../../redux/slices/accountInfo/thunks";
import { Contact } from "../../../types/common";
import { InvoiceState, InvoiceStatus, InvoiceHistory } from "../../../types/invoice";
import { parseAmountString } from "../../../utils/amount";
import { formatToDate, shortenUUID } from "../../../utils/common";
import {
    isNodeDeveloper, isUserContractor,
} from "../../../utils/role";
import ClaimContext from "../../claim/ClaimContext";
import InvoiceStatusDetail from "./InvoiceStatusDetail";
import { PORejectType, PORejectedTable } from "./PORejectedTable";

type InvoiceDetailProps = {
    className?: string;
    invoice: InvoiceState;
    invoiceHistory: InvoiceHistory;
    isPaymentRejected?: boolean;
    hasPaymentAction?: boolean;
};

const InvoiceDetail = ({
    className,
    invoice,
    invoiceHistory,
    isPaymentRejected,
    hasPaymentAction,
}: InvoiceDetailProps): JSX.Element => {
    const currentRoles = useSelector(currentRolesSelector);
    const isDiscarded = invoice.status === InvoiceStatus.CONTRACTOR_DISCARDED;
    const lastComment = invoice.comments[0];

    const { data: claim } = useContext(ClaimContext);
    const developerAccountName = claim?.developer?.accountName;
    const contractorAccountName = claim?.contractor?.accountName;

    const [currentContact, setCurrentContact] = useState<Contact>();
    const dispatch = useDispatch();

    useEffect(() => {
        if (!isUserContractor(currentRoles)) {
            const getContactInfo = async () => {
                const contactResponse = await dispatch(getContactByAccountName(invoice.props.mainContractor)).unwrap();
                const contact: Contact = {
                    company: contactResponse.name,
                    email: contactResponse.contactEmail,
                    contactPerson: contactResponse.contactPerson,
                    phone: contactResponse.contactNumber,
                };
    
                setCurrentContact(contact);
            };
    
            getContactInfo();
        }

        return () => {
            setCurrentContact(undefined);
        };
    }, [currentRoles, invoice, dispatch]);

    return (
        <Card
            className={`mb-5 border-0 ${className || ""} ${isDiscarded ? "text-placeholder-grey" : ""
                }`}
        >
            <Card.Header className="p-0 pb-3">
                <h2 className="mb-0">
                    Invoice Submission{" "}
                    <span className="fs-7 fw-bold">
                        #{shortenUUID(invoice.linearId.id)}
                    </span>
                    <InvoiceStatusDetail
                        roles={currentRoles}
                        invoiceHistory={invoiceHistory}
                        latestStatus={invoice.status}
                        isPaymentRejected={isPaymentRejected}
                        hasPaymentAction={hasPaymentAction}
                    />
                </h2>
            </Card.Header>

            <Card.Body className="px-0 py-0">
                <dl>
                {isNodeDeveloper() && currentContact && (
                    <div>
                        <dt>
                            <StyledH3 isDiscarded={isDiscarded}>
                                Service Provider
                            </StyledH3>
                        </dt>
                        <dd>
                            <p className="mb-2">
                                <span className="ms-2 fw-bold">
                                    {currentContact.company}
                                </span>
                            </p>
                        </dd>
                    </div>
                )
                }
                    <div>
                        <StyledH3 isDiscarded={isDiscarded}>
                            Beneficiary Details
                        </StyledH3>
                        <dd>
                            <p className="mb-2">
                                <span className="h1">
                                    {parseAmountString(invoice.props.amount).amount}
                                </span>
                                <span className="ms-2 fw-bold">
                                    {parseAmountString(invoice.props.amount).currency}
                                </span>
                            </p>
                        </dd>
                        <dt>
                            <StyledLabel className="mb-3" isDiscarded={isDiscarded}>
                                Requested Amount
                            </StyledLabel>
                        </dt>
                    </div>
                    <HorizontalDivider />
                    <Row xs={5}>
                        <Col className="border-end border-humble-grey">
                            <dt>
                                <StyledLabel
                                    className="mt-1 mb-2"
                                    isDiscarded={isDiscarded}
                                >
                                    Creation Date
                                </StyledLabel>
                            </dt>
                            <dd>
                                <p className="fw-bold">
                                    {formatToDate(invoice.createdAt)}
                                </p>
                            </dd>
                        </Col>
                        <Col className="px-4 border-end border-humble-grey">
                            <dt>
                                <StyledLabel
                                    className="mt-1 mb-2"
                                    isDiscarded={isDiscarded}
                                >
                                    Beneficiary Name
                                </StyledLabel>
                            </dt>
                            <dd>
                                <p className="fw-bold mb-0">
                                    {invoice.props.bankAccountDetails.bankAccountHolderName}
                                </p>
                            </dd>
                        </Col>
                        <Col className="px-4 border-end border-humble-grey">
                            <dt>
                                <StyledLabel
                                    className="mt-1 mb-2"
                                    isDiscarded={isDiscarded}
                                >
                                    Beneficiary Bank Account
                                </StyledLabel>
                            </dt>
                            <dd>
                                <p className="fw-bold">
                                    {invoice.props.bankAccountDetails.bankAccountNumberStr}
                                </p>
                            </dd>
                        </Col>
                        <Col className="px-4 border-end border-humble-grey">
                            <dt>
                                <StyledLabel
                                    className="mt-1 mb-2"
                                    isDiscarded={isDiscarded}
                                >
                                    Beneficiary Bank
                                </StyledLabel>
                            </dt>
                            <dd>
                                <p className="fw-bold">
                                    {invoice.props.bankAccountDetails.swiftAddress} -{" "}
                                    {invoice.props.bankAccountDetails.bankName}
                                </p>
                            </dd>
                        </Col>
                        <Col className="px-4">
                            <dt>
                                <StyledLabel
                                    className="mt-1 mb-2"
                                    isDiscarded={isDiscarded}
                                >
                                    Beneficiary Address
                                </StyledLabel>
                            </dt>
                            <dd>
                                <p className="fw-bold">
                                    {invoice.props.bankAccountDetails.registeredAddress}
                                </p>
                            </dd>
                        </Col>
                    </Row>
                    <HorizontalDivider />

                    <StyledH3 className="mt-3" isDiscarded={isDiscarded}>
                        {pluralize("Invoice", invoice.props.documents.length)}
                    </StyledH3>
                    {invoice.props.documents.map((file, index) => (
                        <FileRow
                            key={index}
                            file={file}
                            canDownload
                            downloadMeta={{
                                developerAccountName,
                                contractorAccountName,
                            }}
                        />
                    ))}

                    {lastComment &&
                        !lastComment.resolved &&
                        (invoice.status === InvoiceStatus.PO_REJECTED ||
                            invoice.status === InvoiceStatus.FINANCE_REJECTED) && (
                            <div>
                                <StyledH3
                                    className="mt-5"
                                    isDiscarded={isDiscarded}
                                >
                                    {invoice.status === InvoiceStatus.PO_REJECTED
                                        ? "Project Officer"
                                        : "Finance Department"}
                                </StyledH3>
                                <PORejectedTable
                                    comment={lastComment}
                                    rejectType={PORejectType.INVOICE_REJECT}
                                    status={invoice.status}
                                />
                            </div>
                        )}
                </dl>
            </Card.Body>
        </Card>
    );
};

const StyledH3 = styled.h3<{ isDiscarded?: boolean }>`
    margin-bottom: 1rem;
    color: ${(props) =>
        props.isDiscarded
            ? "var(--bs-placeholder-grey)"
            : "var(--bs-body-color)"};
`;

const StyledLabel = styled.p<{ isDiscarded?: boolean }>`
    color: ${(props) =>
        props.isDiscarded
            ? "var(--bs-placeholder-grey)"
            : "var(--bs-label-grey)"};
`;

export default InvoiceDetail;

